import {
	ActiveExperimentSummary,
	HeatmapSummary,
	InactiveExperimentSummary,
} from '../public/types';
import { addQueryArgs } from '../public/utils/url';

export async function isReachable( url: string ): Promise< boolean > {
	try {
		await fetch( url, {
			mode: 'cors',
			credentials: 'omit',
		} );

		// If we got a response at all → reachable
		return true;
	} catch ( _ ) {
		// Network error, DNS fail, CORS block, etc.
		return false;
	}
}

export function getTestLink(
	homeUrl: string,
	experiment: ActiveExperimentSummary | InactiveExperimentSummary
): string | undefined {
	const control = experiment.alternatives[ 0 ];
	const postId = 'number' === typeof control ? control : 0;

	if ( ! postId ) {
		return undefined;
	}

	if ( experiment.type === 'nab/page' ) {
		return addQueryArgs( homeUrl, { page_id: postId } );
	}

	if (
		experiment.type === 'nab/post' ||
		experiment.type === 'nab/custom-post-type'
	) {
		return addQueryArgs( homeUrl, { p: postId } );
	}

	return undefined;
}

export function getTestName(
	experiment:
		| Pick< ActiveExperimentSummary, 'id' | 'type' | 'name' >
		| Pick< InactiveExperimentSummary, 'id' | 'type' | 'name' >
		| Pick< HeatmapSummary, 'id' | 'name' >,
	mode: 'full' | 'short' = 'full'
): string {
	const type = 'type' in experiment ? experiment.type : 'nab/heatmap';
	const typeName = type
		.replace( /^nab\//, '' )
		.replace( '-', ' ' )
		.split( ' ' )
		.map( ( w ) => w.substring( 0, 1 ).toUpperCase() + w.substring( 1 ) )
		.join( ' ' );
	const prefix = 'nab/heatmap' === type ? 'Heatmap' : `${ typeName } Test`;

	if ( 'full' === mode && experiment.name ) {
		return `${ prefix } #${ experiment.id } — ${ experiment.name }`;
	}

	return `${ prefix } #${ experiment.id }`;
}

export function getGoalName(
	goal: { name?: string } | undefined,
	index: number
): string {
	const prefix = `${ 0 === index ? 'Default' : ordinal( index + 1 ) } Goal`;
	return goal?.name ? `${ prefix } — ${ goal.name }` : prefix;
}

export function getSegmentName(
	segment: { name?: string } | undefined,
	index: number
): string {
	const prefix = `${ ordinal( index + 1 ) } Segment`;
	return segment?.name ? `${ prefix } — ${ segment.name }` : prefix;
}

// ========
// INTERNAL
// ========

function ordinal( value: number ): string {
	if ( 1 === value % 10 ) {
		return `${ value }st`;
	}

	if ( 2 === value % 10 ) {
		return `${ value }nd`;
	}

	if ( 3 === value % 10 ) {
		return `${ value }rd`;
	}

	return `${ value }th`;
}
